import { animate, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, output, signal, ViewEncapsulation } from '@angular/core';
import { ToastData } from './toast.service';

/** How long a toast stays before it closes on its own (paused while hovered or focused). */
export const DEFAULT_TOAST_DURATION_MS = 2000;

const ICONS: Record<ToastData['type'], string> = {
  success: 'M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  error: 'M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z',
  warning:
    'M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z',
  info: 'M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z',
  debug:
    'M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655-4.653a2.548 2.548 0 010-3.586l.823-.823a2.548 2.548 0 013.586 0l4.655 4.653M3.75 12l8.003-8.003a1.125 1.125 0 011.591 0l2.659 2.659a1.125 1.125 0 010 1.591L7.5 16.5',
};

/**
 * One toast in the stack. Errors and warnings stay until dismissed; other
 * toasts close after their duration, which pauses while the toast is hovered
 * or focused and while the window is hidden. The container announces the
 * text, so the toast itself carries no live role.
 */
@Component({
  selector: 'app-toast',
  templateUrl: './toast.component.html',
  styleUrls: ['toast.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('toastAnimation', [
      transition(':enter', [style({ opacity: 0, transform: 'translateY(8px)' }), animate('150ms ease-out', style({ opacity: 1, transform: 'none' }))]),
      transition(':leave', [animate('120ms ease-in', style({ opacity: 0 }))]),
    ]),
  ],
})
export class ToastComponent {
  private readonly destroyRef = inject(DestroyRef);

  readonly data = input.required<ToastData>();
  /** Position in the stack, newest first; kept for the container's ordering. */
  readonly index = input<number>(0);
  readonly closed = output<void>();

  readonly toastClass = computed(() => `toast-item toast-${this.data().type}`);
  readonly iconPath = computed(() => ICONS[this.data().type] ?? ICONS.info);

  /** Every toast times out unless it is shown with a duration of 0. */
  readonly persistent = computed(() => this.progressDuration() <= 0);

  /** Spoken label of the toast's type, read before its text. */
  readonly typeLabel = computed(() => {
    switch (this.data().type) {
      case 'error':
        return 'Error';
      case 'warning':
        return 'Warning';
      case 'success':
        return 'Success';
      default:
        return 'Information';
    }
  });

  private readonly hovered = signal(false);
  private readonly focused = signal(false);
  private readonly hidden = signal(typeof document !== 'undefined' && document.visibilityState === 'hidden');

  /** The timer stops while the toast is hovered, focused or the window is hidden. */
  readonly paused = computed(() => this.hovered() || this.focused() || this.hidden());

  private remainingMs: number | null = null;
  private startedAt = 0;
  private timeoutId: ReturnType<typeof setTimeout> | null = null;

  readonly progressDuration = computed(() => this.data().duration ?? this.getDefaultDuration());

  constructor() {
    effect(() => {
      if (this.persistent()) return;
      if (this.paused()) {
        this.stopTimer();
      } else {
        this.startTimer();
      }
    });
    if (typeof document !== 'undefined') {
      const onVisibility = (): void => this.hidden.set(document.visibilityState === 'hidden');
      document.addEventListener('visibilitychange', onVisibility);
      this.destroyRef.onDestroy(() => document.removeEventListener('visibilitychange', onVisibility));
    }
    this.destroyRef.onDestroy(() => this.stopTimer());
  }

  close(): void {
    this.closed.emit();
  }

  runAction(): void {
    const action = this.data().action;
    this.close();
    action?.run();
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.stopPropagation();
      this.close();
    }
  }

  onPointerEnter(): void {
    this.hovered.set(true);
  }

  onPointerLeave(): void {
    this.hovered.set(false);
  }

  onFocusIn(): void {
    this.focused.set(true);
  }

  onFocusOut(event: FocusEvent): void {
    const next = event.relatedTarget;
    const host = event.currentTarget;
    if (next instanceof Node && host instanceof Node && host.contains(next)) return;
    this.focused.set(false);
  }

  private startTimer(): void {
    if (this.timeoutId !== null) return;
    this.remainingMs ??= this.progressDuration();
    this.startedAt = Date.now();
    this.timeoutId = setTimeout(() => this.close(), this.remainingMs);
  }

  private stopTimer(): void {
    if (this.timeoutId === null) return;
    clearTimeout(this.timeoutId);
    this.timeoutId = null;
    this.remainingMs = Math.max(0, (this.remainingMs ?? 0) - (Date.now() - this.startedAt));
  }

  private getDefaultDuration(): number {
    return DEFAULT_TOAST_DURATION_MS;
  }
}
